import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ProductReviews({ product }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/reviews/${product.id}`).then((r) => setReviews(r.data));
  useEffect(() => { if (product?.id) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [product?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to leave a review"); return; }
    if (!comment.trim()) { toast.error("Comment can't be empty"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: product.id, rating, title, comment });
      toast.success("REVIEW SUBMITTED", { description: "Pending admin approval — it'll appear once reviewed." });
      setTitle(""); setComment(""); setRating(5);
      // Don't reload — new review is pending and won't be in the public list anyway
    } catch (err) {
      toast.error("Failed", { description: err?.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 border-t border-[#27272A]" data-testid="product-reviews">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="label-mono text-[#71717A]">// CUSTOMER REVIEWS</p>
          <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
            What the Cult says
          </h2>
        </div>
        {avg && (
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={20} className={s <= Math.round(avg) ? "fill-[#CFF048] text-[#CFF048]" : "text-[#27272A]"} />
              ))}
            </div>
            <span className="font-mono-cult text-[#CFF048] text-2xl font-bold">{avg}</span>
            <span className="label-mono text-[#71717A]">// {reviews.length} REVIEW{reviews.length !== 1 ? "S" : ""}</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Reviews list */}
        <div className="lg:col-span-2 space-y-4">
          {reviews.length === 0 && (
            <div className="border border-[#27272A] p-8 text-center label-mono text-[#71717A]">
              // NO REVIEWS YET. BE THE FIRST.
            </div>
          )}
          {reviews.map((r) => (
            <div key={r.id} className="border border-[#27272A] p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-[#CFF048] text-black font-display font-black flex items-center justify-center">
                    {(r.user_name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-display font-bold text-sm">{r.user_name || "Anonymous"}</p>
                    <p className="label-mono text-[#71717A]">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={14} className={s <= r.rating ? "fill-[#CFF048] text-[#CFF048]" : "text-[#27272A]"} />
                  ))}
                </div>
              </div>
              {r.title && <p className="font-display font-bold mt-3">{r.title}</p>}
              <p className="text-[#A1A1AA] text-sm mt-2 leading-relaxed">{r.comment}</p>
            </div>
          ))}
        </div>

        {/* Review form */}
        <form onSubmit={submit} className="border border-[#27272A] p-6 h-fit lg:sticky lg:top-24" data-testid="review-form">
          <p className="label-mono text-[#CFF048]">// LEAVE A REVIEW</p>
          {!user ? (
            <p className="text-[#A1A1AA] mt-4 text-sm">Sign in to leave a review.</p>
          ) : (
            <>
              <div className="mt-4">
                <p className="label-mono text-[#71717A] mb-2">RATING</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onMouseEnter={() => setHover(s)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(s)}
                      data-testid={`rating-star-${s}`}
                    >
                      <Star
                        size={26}
                        className={s <= (hover || rating) ? "fill-[#CFF048] text-[#CFF048]" : "text-[#27272A]"}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="TITLE (OPTIONAL)"
                className="cult-input mt-4"
                data-testid="review-title"
              />
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="HOW WAS IT?"
                rows={4}
                className="cult-input mt-3"
                data-testid="review-comment"
              />
              <button type="submit" disabled={busy} className="btn-neon w-full mt-4" data-testid="submit-review-btn">
                {busy ? "POSTING..." : "POST REVIEW"}
              </button>
              <p className="label-mono text-[#71717A] mt-3 text-[10px] leading-relaxed">
                // REVIEWS ARE MODERATED — YOURS WILL APPEAR AFTER ADMIN APPROVAL.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
